import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, CircularProgress } from '@mui/material'
import { Helmet } from 'react-helmet-async'
import { Navbar } from '../../component/shared/Navbar'
import { PageContainer } from '../../component/styled/PageContainer'
import { useAuth } from '../../api/context/UserAuthContext'
import ErrorMessage from '../../component/shared/component/ErrorMessage'
import { SearchFilter } from '../../component/point/SearchFilter'
import DownloadIcon from '@mui/icons-material/Download'
import { useSnackbar } from 'notistack'
import { PaginationTable } from './PaginationTable'
import { SalesFilter } from './SalesFilter'

export const PointPanelScreen = ({
  title,
  htgCompany,
  useExportApi,
  useGetPoints,
  columns,
  linkBase,
  customComponent: CustomComponent,
  tableSx,
}) => {
  const { user } = useAuth()
  const [salesIds, setSalesIds] = useState([])
  const [searchFilter, setSearchFilter] = useState('')
  const { enqueueSnackbar } = useSnackbar()
  const exportApi = useExportApi()

  const handleExport = async () => {
    const res = await exportApi
      .mutateAsync({})
      .catch((e) => enqueueSnackbar('Failed to export data', { variant: 'error' }))
    const url = URL.createObjectURL(res.data)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', title)
    document.body.appendChild(link)
    link.click()
    if (res?.status === 200) enqueueSnackbar(`Exported`, { variant: 'success' })
    return res
  }

  useEffect(() => {
    if (!user?.isPointSystemAdmin && user?.salesIds) {
      setSalesIds(user?.salesIds)
    }
  }, [user])

  return (
    <>
      <Helmet titleTemplate='%s | Point System' defaultTitle='Point System'>
        <title>{title}</title>
      </Helmet>
      <Navbar />
      <PageContainer>
        <Box
          display='flex'
          justifyContent='space-between'
          flexDirection='column'
          px={{ xs: 3, md: 8 }}
          pt={3}
        >
          <Typography variant='h1'>{title}</Typography>
          <Box
            display='flex'
            alignItems={{ sm: 'center' }}
            gap={1}
            mt={1}
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            <SearchFilter onChange={setSearchFilter} />
            {user?.isPointSystemAdmin && (
              <SalesFilter salesIds={salesIds} setSalesIds={setSalesIds} htgCompany={htgCompany} />
            )}
          </Box>
          {user?.isPointSystemAdmin && (
            <Box display='flex' justifyContent='flex-end'>
              <Button
                startIcon={<DownloadIcon />}
                endIcon={exportApi.isLoading && <CircularProgress size='1.5rem' />}
                onClick={handleExport}
                disabled={exportApi.isLoading}
              >
                Export Data
              </Button>
            </Box>
          )}
          {CustomComponent && <CustomComponent />}
        </Box>
        <Box pt={3} pb={10} px={{ xs: 3, md: 8 }}>
          {user?.isPointSystemAdmin || salesIds ? (
            <PaginationTable
              useGetPoints={useGetPoints}
              columns={columns}
              salesIds={salesIds}
              searchFilter={searchFilter}
              linkBase={linkBase}
              tableSx={tableSx}
            />
          ) : (
            <ErrorMessage error message={'Need admin permission or sales id'} />
          )}
        </Box>
      </PageContainer>
    </>
  )
}
